// public/js/features/assets/components/asset-action-menu.js

import { positionDropdown } from '../../../modules/ui.js';
import { escapeHtml } from '../../../utils/escape.js';
import { deleteAsset } from '../api.js';
import { can, getCurrentUser } from '../../../modules/rbac.js';
import { openUploader } from '../../files/uploader.js';
import { listDocTypes } from '../../files/api.js';

export function showActionMenu(e, asset, refreshCallback) {
  // Quickest path to attach something to an asset — straight from the list row,
  // no detour via the detail page (docs/asset-files.md §9).
  const canUpload = can(getCurrentUser().groups || [], 'manageAssetFiles');
  const menuHTML = `
<div class="dropdown-menu show shadow" style="position:absolute; min-width:220px; z-index:1050;">
    <a class="dropdown-item" href="#" onclick="window.viewAssetOnMap('${asset.assetUUID}'); return false;">
      <i class="fa-solid fa-map me-2"></i>View on Map
    </a>
      <a class="dropdown-item edit-asset" href="#">✏️ Edit Asset</a>
      ${canUpload ? `
      <div class="dropdown-divider"></div>
      <a class="dropdown-item add-photo" href="#"><i class="fa-solid fa-camera me-2"></i>Add photo</a>
      <a class="dropdown-item add-file" href="#"><i class="fa-solid fa-paperclip me-2"></i>Add document</a>` : ''}
      <div class="dropdown-divider"></div>
      <a class="dropdown-item text-danger delete-asset" href="#">🗑️ Delete Asset (incl. history)</a>
    </div>`;

  const menuContainer = document.createElement('div');
  menuContainer.innerHTML = menuHTML;
  const dropdown = menuContainer.firstElementChild;
  document.body.appendChild(dropdown);

  // === IMPORTANT: intelligent positioning like for devices ===
  positionDropdown(dropdown, e.target, 220);

  const closeMenu = () => {
    if (dropdown && dropdown.parentNode) dropdown.parentNode.removeChild(dropdown);
  };
  setTimeout(() => document.addEventListener('click', closeMenu, { once: true }), 10);

  // "View on Map" navigates via its own inline onclick (window.viewAssetOnMap +
  // return false). The other two items had NO handler at all — so clicking "Edit
  // Asset" just followed href="#", which the hash-router turned into a jump to the
  // map, and "Delete Asset" did nothing. Wire them here.
  dropdown.querySelector('.edit-asset')?.addEventListener('click', (ev) => {
    ev.preventDefault();
    closeMenu();
    window.showAssetDetail?.(asset.assetUUID);
  });
  dropdown.querySelector('.delete-asset')?.addEventListener('click', (ev) => {
    ev.preventDefault();
    closeMenu();
    confirmAndDeleteAsset(asset, refreshCallback);
  });

  const openUploadFor = async (kind) => {
    // Doc types are only needed for documents; a photo has no type.
    let docTypes = [];
    if (kind === 'file' && asset.tenantId) {
      try { docTypes = (await listDocTypes(asset.tenantId)).filter(t => t.enabled); } catch {}
    }
    openUploader({ assetUUID: asset.assetUUID, kind, docTypes, onDone: () => refreshCallback?.() });
  };
  dropdown.querySelector('.add-photo')?.addEventListener('click', (ev) => {
    ev.preventDefault(); closeMenu(); openUploadFor('photo');
  });
  dropdown.querySelector('.add-file')?.addEventListener('click', (ev) => {
    ev.preventDefault(); closeMenu(); openUploadFor('file');
  });
}

async function confirmAndDeleteAsset(asset, refreshCallback) {
  const modalHTML = `
    <div class="modal fade" id="deleteAssetModal" tabindex="-1">
      <div class="modal-dialog modal-dialog-centered">
        <div class="modal-content bg-dark text-white border-danger">
          <div class="modal-header border-danger">
            <h5 class="modal-title"><i class="fa-solid fa-exclamation-triangle text-danger me-2"></i>Delete asset?</h5>
            <button type="button" class="btn-close btn-close-white" data-bs-dismiss="modal"></button>
          </div>
          <div class="modal-body">
            <p><strong>${escapeHtml(asset.label || asset.name || asset.assetUUID)}</strong> (${escapeHtml(asset.assetUUID || asset.assetId)}) will be <strong>permanently deleted</strong>.</p>
            <div class="alert alert-warning small py-2">
              The entire position history will also be deleted.<br>
              Assigned devices will be released automatically.
            </div>
          </div>
          <div class="modal-footer border-danger">
            <button type="button" class="btn btn-secondary" data-bs-dismiss="modal">Cancel</button>
            <button id="btn-do-delete-asset" class="btn btn-danger">
              <i class="fa-solid fa-trash me-2"></i> Yes, delete asset + history
            </button>
          </div>
        </div>
      </div>
    </div>`;

  document.body.insertAdjacentHTML('beforeend', modalHTML);
  const modalEl = document.getElementById('deleteAssetModal');
  const modal = new bootstrap.Modal(modalEl);
  modal.show();

  document.getElementById('btn-do-delete-asset').onclick = async () => {
    const btn = document.getElementById('btn-do-delete-asset');
    btn.disabled = true;
    btn.innerHTML = `<span class="spinner-border spinner-border-sm me-2"></span>Deleting...`;

    try {
      await deleteAsset(asset.assetUUID || asset.assetId);
      modal.hide();
      window.showToast(`Asset "${asset.label || asset.name || asset.assetUUID}" was deleted.`, 'success');
      if (refreshCallback) await refreshCallback();
    } catch (e) {
      console.error(e);
      window.showToast('Error deleting asset', 'danger');
    } finally {
      modalEl.remove();
    }
  };

  modalEl.addEventListener('hidden.bs.modal', () => modalEl.remove(), { once: true });
}